import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Heart, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | pending | error
  const [details, setDetails] = useState(null);
  const polledRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      polledRef.current += 1;
      try {
        const { data } = await axios.get(
          `${API}/checkout/status/${sessionId}`
        );
        setDetails(data);
        if (data.payment_status === "paid" || data.status === "complete") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }
        if (polledRef.current >= 10) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (polledRef.current >= 5) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-5"
      style={{ backgroundColor: "#E8E6F5" }}
      data-testid="success-page"
    >
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-12 h-12 text-slate-700 animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-5">
              Confirming your donation…
            </h1>
            <p className="text-slate-500 mt-2">This will only take a moment.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-[#EFEBFA] flex items-center justify-center">
              <CheckCircle2
                className="w-10 h-10 text-[#0F172A]"
                strokeWidth={2.5}
              />
            </div>
            <h1
              className="font-heading text-3xl font-extrabold text-slate-900 mt-5"
              data-testid="success-title"
            >
              Thank you!
            </h1>
            <p className="text-slate-600 mt-2 leading-relaxed">
              Your donation
              {details?.amount_total
                ? ` of $${(details.amount_total / 100).toFixed(2)} ${(details.currency || "AUD").toUpperCase()}`
                : ""}{" "}
              {details?.purpose_title ? `for ${details.purpose_title} ` : ""}
              has been received.
              {details?.monthly &&
                " You'll be charged the same amount each month until you cancel."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-slate-700">
              <Heart className="w-5 h-5" />
              <span className="font-semibold">May Allah reward you.</span>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-slate-700 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-5">
              Payment processing
            </h1>
            <p className="text-slate-500 mt-2">
              We'll email you a receipt once it's confirmed.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900">
              We couldn't verify your payment
            </h1>
            <p className="text-slate-500 mt-2">
              If you were charged, please contact us — we'll sort it out.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="back-to-donate"
          className="mt-7 inline-flex items-center gap-2 text-slate-900 font-semibold hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to donate page
        </Link>
      </div>
    </div>
  );
}
